import { useAuth0 } from "@auth0/auth0-react";
import { parse } from "papaparse";
import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import config from "../../config";
import { capitalize, isEmpty } from "../../utils";

export const AUTH0_CLIENT_ID = config.client_mapping || "auth0_client_id";
export const GENERIC_ERROR_MESSAGE = "Something went wrong.";
// const FQS_API_URL = `${
//   config.client_mapping[window.location.hostname] &&
//   config.client_mapping[window.location.hostname].fqs_api_endpoint
// }`;

const USR_MGMT_ENDPOINT = `${config.usr_mgmt_endpoint}`;

const Atom_proxy = config.registry_api_endpoint;

// List-tables only. A 503 means the datasource connection is still starting
// (cold Lambda); retry that call. Any other status, including 500, is final.
const TABLES_LIST_MAX_RETRIES = 4;
const TABLES_LIST_DEFAULT_RETRY_AFTER_SECONDS = 15;

const abortError = () => {
  const error = new Error("Aborted");
  error.name = "AbortError";
  return error;
};

const waitWithAbort = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    let settled = false;
    let timer: ReturnType<typeof setTimeout>;
    const onAbort = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(abortError());
    };
    timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort);
  });

const identifiers = config.csv_identifiers;

// organisation-month-level-count wraps its rows as { data: { organisationMonth: [...] } }
// instead of a flat { data: [...] } like its federation/adserver siblings — flatten it here
// so callers can treat all three "*-month-level-count" responses the same way.
const normalizeOrganisationMonthResponse = (json: any) => {
  if (
    json &&
    !Array.isArray(json.data) &&
    Array.isArray(json.data?.organisationMonth)
  ) {
    return { ...json, data: json.data.organisationMonth };
  }
  return json;
};

// Module-level cache so the config fetch only happens once per org per session
const _identityTypesCache = new Map<
  string,
  Array<{ key: string; value: string }>
>();

const initialState: any = {
  email: "",
  appMetadata: undefined,
};

export const AuthContext = React.createContext(initialState);
export const AuthProvider = (props) => {
  const [vaultAddress, setVaultAddress] = useState("");
  const [rbacAccessDenied, setRbacAccessDenied] = useState(false);

  const {
    isAuthenticated,
    isLoading,
    user,
    getIdTokenClaims,
    loginWithRedirect,
    logout = (options?: any) => {},
  } = useAuth0();
  useEffect(() => {
    handleAuth();
    // eslint-disable-next-line
  }, [isLoading]);
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      // If page is loaded from bfcache and user is not authenticated
      if (event.persisted && !isAuthenticated) {
        // Force reload or redirect
        window.location.reload();
        // OR redirect to login
        // window.location.href = '/home';
      }
    };
    const handlePageHide = () => {
      // Clear sensitive data before page is cached
      if (!isAuthenticated) {
        setVaultAddress("");
        // Clear other sensitive state
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [isAuthenticated]);
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && !isAuthenticated) {
        // Clear sensitive data when page becomes visible
        setVaultAddress("");
        // Clear other state...
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated]);
  useEffect(() => {
    // if (isAuthenticated) {
    //   getVaultAddress({args: {}, signal: undefined});
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const loginTime =
      user?.["https://users/app_metadata"]?.activeSessions?.[0]?.loginTime;
    if (!loginTime) return;
    setSessionLoginTime(loginTime);
  }, [isAuthenticated, user]);

  const [sessionLoginTime, setSessionLoginTime] = React.useState<string | null>(
    null,
  );

  const MAX_SESSION_MS = 180 * 60 * 1000;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isAuthenticated || !sessionLoginTime) return;
  }, [isAuthenticated, sessionLoginTime]);

  const sessionTimerRef = React.useRef<number | null>(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isAuthenticated || !sessionLoginTime) return;
    const loginTime = sessionLoginTime;
    if (!loginTime) return;
    if (sessionTimerRef.current) {
      clearTimeout(sessionTimerRef.current);
      sessionTimerRef.current = null;
    }
    const loginTs = Date.parse(loginTime);
    const expiryTs = loginTs + MAX_SESSION_MS;
    let remainingMs = expiryTs - Date.now();
    if (remainingMs < 0) {
      remainingMs = MAX_SESSION_MS;
    }
    sessionTimerRef.current = window.setTimeout(() => {
      console.log("Session expired → logout");
      clearAuth();
    }, remainingMs);
    return () => {
      if (sessionTimerRef.current) {
        clearTimeout(sessionTimerRef.current);
        sessionTimerRef.current = null;
      }
    };
  }, [isAuthenticated, sessionLoginTime]); // eslint-disable-line react-hooks/exhaustive-deps

  console.log("Auth0 client id", AUTH0_CLIENT_ID);
  const tenants =
    user &&
    user[`https://users/app_metadata`][AUTH0_CLIENT_ID] &&
    user[`https://users/app_metadata`][AUTH0_CLIENT_ID]["tenants"];
  const tenantId = tenants && Object.keys(tenants)[1];
  const applicationId: string | null = (() => {
    if (!tenants) return null;
    const str = JSON.stringify(tenants);
    const match = str.match(/"application_id"\s*:\s*"([^"]+)"/);
    return match ? match[1] : null;
  })();
  console.log("tenants", tenants);
  console.log("tenantId", tenantId);
  const appMetadata = user && user["https://users/app_metadata"];
  const loginTime = appMetadata?.activeSessions?.[0]?.loginTime;
  console.log("loginTime", loginTime);
  const handleAuth = async () => {
    if (!isLoading) {
      if (!isAuthenticated) {
        loginWithRedirect();
        return;
      }
      try {
        if (!tenants || Object.keys(tenants).length === 0) {
          alert(
            "You are signed out since you are not allowed to use this service.",
          );
          throw new Error("Not Allowed User");
        }
      } catch (error) {
        console.error(error);
        clearAuth();
      }
    }
  };

  const handleError = (res) => {
    if (res.status === 401) {
      clearAuth();
    }
    throw new Error(`Error ${res.status}`);
  };

  const handleAndParseError = async (res) => {
    if (res.status === 401) {
      clearAuth();
    }
    let message: string | null = await res.text().catch(() => null);
    try {
      const firstBraceIndex = message.indexOf("{");
      const lastBraceIndex = message.lastIndexOf("}");
      const jsonString = message
        .substring(firstBraceIndex, lastBraceIndex + 1)
        .trim()
        .replaceAll(`\\"`, `"`);
      const errorObject = JSON.parse(jsonString);
      const extracted =
        findKeyInObject(errorObject, "message") ||
        findKeyInObject(errorObject, "error");
      message = extracted ? capitalize(extracted.replace(/_/g, " ")) : message;
    } catch (error) {
      console.error("Unable to parse error message from response", error);
    }
    throw new Error(message);
  };

  function findKeyInObject(obj, targetKey) {
    if (obj === null || typeof obj !== "object") {
      return null;
    }
    if (obj[targetKey]) {
      return obj[targetKey];
    }

    for (const key in obj) {
      if (obj[key]) {
        const value = obj[key];
        if (typeof value === "object" && value !== null) {
          const found = findKeyInObject(value, targetKey);
          if (found !== null) {
            return found;
          }
        }
      }
    }
    return null;
  }

  const getUsers = async ({ signal }) => {
    const res = await fetch(
      `${USR_MGMT_ENDPOINT}/api/v1/users?tenant_id=${tenantId}`,
      {
        method: "GET",
        headers: await getHeaders(),
        signal,
      },
    );
    if (!res.ok) {
      handleError(res);
    }
    const data = await res.json();
    return data;
  };

  const putUser = async ({ args, signal }) => {
    const { body, reloadUsers } = args;
    const res = await fetch(
      `${USR_MGMT_ENDPOINT}/api/v1/users/${body.email}?tenant_id=${tenantId}`,
      {
        method: "PUT",
        headers: await getHeaders(),
        body: JSON.stringify(body),
        signal,
      },
    );
    if (!res.ok) {
      handleError(res);
    }
    if (reloadUsers !== undefined) {
      reloadUsers();
    }
    const data = await res.json();
    return data;
  };

  const clearAuth = () => {
    localStorage.clear();
    sessionStorage.clear();

    logout({
      logoutParams: {
        returnTo: `${config.clientAppUrl}`,
      },
    });
  };

  const getHeaders = async () => {
    const requestId = uuidv4();
    const idTokenClaims = await getIdTokenClaims();
    const idToken = idTokenClaims["__raw"];
    return {
      Accept: "*/*",
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
      "Cache-Control": "no-store, no-cache, must-revalidate, proxy-revalidate",
      pragma: "no-cache",
      Expires: "0",
      "x-client-id": AUTH0_CLIENT_ID,
      "x-request-id": requestId,
    };
  };

  // const getVaultAddress = async ({ args, signal }) => {
  //   const res = await fetch(`${FQS_API_URL}/api/v1/vaults/${tenantId}`, {
  //     method: "GET",
  //     headers: await getHeaders(),
  //     signal,
  //   });
  //   if (!res.ok) {
  //     handleError(res);
  //   }
  //   const data = await res.json();
  //   // Warning: Because of setState in auth-context, getVaultAddress should be triggered only once, and not in any other part of the application so as to avoid infinite re-renders.
  //   setVaultAddress(data.vault_addr);
  //   return data;
  // };

  const getUploadStatus = async ({ args, signal }) => {
    const res = await fetch(
      `https://${vaultAddress}/api/v1/schema/default_schema/upload_status`,
      {
        method: "GET",
        headers: await getHeaders(),
        signal,
      },
    );
    if (!res.ok) {
      handleError(res);
    }
    const data = await res.json();
    return data;
  };

  const getNumRecords = async ({ args }) => {
    const { userBucketId, organizationId } = args;
    const url =
      `${Atom_proxy}/api/v1/spa/fqs/stats/buckets/${userBucketId}/count?` +
      new URLSearchParams({ organization_id: organizationId });
    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });
    if (!res.ok) {
      handleError(res);
    }
    const data = await res.json();
    return data;
  };
  const getrecompute = async ({ args }) => {
    const { userBucketId, organizationId } = args;
    const url =
      `${Atom_proxy}/api/v1/spa/fqs/buckets/${userBucketId}/stats/recompute?` +
      new URLSearchParams({ organization_id: organizationId });
    const res = await fetch(url, {
      method: "POST",
      headers: await getHeaders(),
    });
    if (!res.ok) {
      handleError(res);
    }
    const data = await res.json();
    return data;
  };

  const patchSchema = async ({ args, signal }) => {
    const { isLive, changeList, schemaName } = args;
    const body: any = {};
    if (isLive !== undefined) {
      body.is_live = isLive;
    }
    if (!isEmpty(changeList)) {
      body.attributes = changeList;
    }
    const res = await fetch(
      `https://${vaultAddress}/api/v1/schema/${schemaName}`,
      {
        method: "PATCH",
        headers: await getHeaders(),
        signal,
        body: JSON.stringify(body),
      },
    );
    if (!res.ok) {
      handleError(res);
    }
    const data = await res.json();
    return data;
  };
  const deleteSchema = async ({ args, signal }) => {
    const { userBucketId, organizationId } = args;
    const url =
      `${Atom_proxy}/api/v1/spa/fqs/buckets/${userBucketId}?` +
      new URLSearchParams({ organization_id: organizationId });
    const res = await fetch(url, {
      method: "DELETE",
      headers: await getHeaders(),
      signal,
    });
    if (!res.ok) {
      handleError(res);
    }
    const data = await res.json();
    return data;
  };
  const getBucketsCounts = async ({ args }) => {
    const { federationId, organizationId } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/buckets?` +
      new URLSearchParams({
        federation_id: federationId,
        organization_id: organizationId,
      });

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const deleteDatasource = async ({ args, signal }) => {
    const { datasourceId } = args;
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/aqs/data-sources/${datasourceId}`,
      {
        method: "DELETE",
        headers: await getHeaders(),
        signal,
      },
    );
    if (!res.ok) {
      handleError(res);
    }
    const data = await res.json();
    return data;
  };
  // Fetches canonical_identity_types from config once per org, then caches.
  const resolveIdentityTypesMap = async (
    organizationId: string,
  ): Promise<Map<string, string>> => {
    if (_identityTypesCache.has(organizationId)) {
      return new Map(
        _identityTypesCache
          .get(organizationId)!
          .map(({ key, value }) => [key, value]),
      );
    }
    const configUrl =
      `${Atom_proxy}/api/v1/spa/fqs/getconfig?` +
      new URLSearchParams({
        organization_id: organizationId,
        client: "application",
      });
    try {
      const configRes = await fetch(configUrl, {
        method: "GET",
        headers: await getHeaders(),
      });
      if (configRes.ok) {
        const configData = await configRes.json();
        const types: Array<{ key: string; value: string }> =
          configData?.message?.canonical_identity_types ?? [];
        _identityTypesCache.set(organizationId, types);
        return new Map(types.map(({ key, value }) => [key, value]));
      }
    } catch {
      // fall through to empty map on error
    }
    return new Map();
  };

  const getGlobalSchema = async ({ args }) => {
    const { federationId, organizationId } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/globalschema?last_key&` +
      new URLSearchParams({
        federation_id: federationId,
        organization_id: organizationId,
      });

    const [res, identityMap] = await Promise.all([
      fetch(url, { method: "GET", headers: await getHeaders() }),
      resolveIdentityTypesMap(organizationId),
    ]);

    if (!res.ok) {
      handleError(res);
    }

    const data = await res.json();
    return {
      ...data,
      canonical_identity_types: (data.canonical_identity_types ?? []).map(
        (key: string) => ({ key, value: identityMap.get(key) ?? key }),
      ),
    };
  };

  const getGlobalSchemawithFilters = async ({ args }) => {
    const {
      federationId,
      organizationId,
      identifier,
      canonical_identity_type,
    } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/globalschema?last_key&` +
      new URLSearchParams({
        federation_id: federationId,
        organization_id: organizationId,
      }) +
      `&identifier=${identifier}&canonical_identity_type=${canonical_identity_type}`;

    const [res, identityMap] = await Promise.all([
      fetch(url, { method: "GET", headers: await getHeaders() }),
      resolveIdentityTypesMap(organizationId),
    ]);

    if (!res.ok) {
      handleError(res);
    }

    const data = await res.json();
    return {
      ...data,
      canonical_identity_types: (data.canonical_identity_types ?? []).map(
        (key: string) => ({ key, value: identityMap.get(key) ?? key }),
      ),
    };
  };

  const getBucketNames = async ({ args }) => {
    const { federationId, IdentifierType, organizationId } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/buckets?` +
      new URLSearchParams({
        federation_id: federationId,
        organization_id: organizationId,
      }) +
      `&identifier_type=${IdentifierType}` +
      `&page=query`;

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const uploadCsv = ({ args, signal }) => {
    const {
      csvFile,
      uploadId,
      numRows,
      setProcessedBytes,
      setUploadedBytes,
      worker,
    } = args;

    const maxChunkSize = 20000000; // 20MB

    console.log("Starting upload timer for uploadCSV...");
    console.time("uploadCSV");
    const responseArray = new Promise((resolve, reject) => {
      worker.addEventListener("error", (e) => {
        console.error(e);
        reject(e);
      });
      worker.addEventListener("message", (e) => {
        if (e.data && e.data.log) {
          console.log(e.data.log);
          return;
        }
        if (e.data && e.data.processedBytes) {
          console.log(
            "Update processed bytes progress: ",
            e.data.processedBytes,
          );
          setProcessedBytes(e.data.processedBytes);
          return;
        }
        if (e.data && e.data.uploadedBytes) {
          console.log("Update uploaded bytes progress: ", e.data.uploadedBytes);
          setUploadedBytes((cur) => cur + e.data.uploadedBytes);
          return;
        }

        const res = JSON.parse(e.data);
        console.timeEnd("uploadCSV");
        if (res && res.isError) {
          console.error(res.message);
          reject(res.message);
        } else {
          resolve(res);
        }
      });
      parse(csvFile, {
        header: true,
        fastMode: true,
        dynamicTyping: true,
        skipEmptyLines: true,
        worker: true,
        chunk: async (results, parser) => {
          const headers = await getHeaders();
          // delete headers['Content-Type'];
          headers["x-upload-id"] = uploadId;

          worker.postMessage(
            JSON.stringify({
              results,
              identifiers,
              fileSize: csvFile.size,
              maxChunkSize,
              // position of last byte of chunk in file.
              // E.g. fileSize = 7KB, chunksize = 3KB, total chunks = 3, current chunk = 3,
              // On chunk 2, cursorPosition = 2 * 3000 = 6000
              // On chunk 3, cursorPosition = 2 * 3000 + 1000 = 7000
              cursorPosition: results.meta.cursor,
              headers,
              vaultAddress,
              numRows,
            }),
          );
        },
        chunkSize: maxChunkSize,
        error: (error) => {
          console.error("file parsing error", error);
          reject(error);
        },
      });
    });
    return responseArray;
  };

  const getProvenanceTrialEvents = async ({ args }) => {
    const { federationId, organizationId } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/events?` +
      new URLSearchParams({
        federation_id: federationId,
        organization_id: organizationId,
      });

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const getProvenanceTrail = async ({ args }) => {
    const {
      federation_id,
      offset,
      limit,
      sort = "desc",
      filterOrgId,
      organization_id,
      event,
      query_id,
      start_date,
      end_date,
      user_name,
      role,
      fromOrganization,
    } = args;

    const userRole = localStorage.getItem("userRole");
    if (
      userRole !== config.superUserRole &&
      userRole !== config.applicationAdminRole &&
      !organization_id &&
      !filterOrgId
    )
      return;

    const url =
      `${Atom_proxy}/api/v1/spa/index/getactivities?` +
      new URLSearchParams({
        federation_id,
        organization_id,
      }) +
      `&offset=${offset}` +
      `&limit=${limit}` +
      `&sort=${sort}` +
      `&event=${event}` +
      `&query_id=${query_id}` +
      `&user_name=${user_name}` +
      `&start_date=${start_date}` +
      `&end_date=${end_date}` +
      `&role=${role}` +
      `&fromOrganization=${fromOrganization}` +
      `&filter_organization_id=${filterOrgId}`;

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const getDashboard = async ({ args, signal }) => {
    const {
      start_date,
      end_date,
      keys,
      federation_ids,
      organization_ids,
    } = args || {};
    const params = new URLSearchParams({ start_date, end_date });
    if (keys?.length) params.append("keys", keys.join(","));
    if (federation_ids?.length)
      params.append("filter_federation_ids", federation_ids.join(","));
    if (organization_ids?.length)
      organization_ids.forEach((id: string) =>
        params.append("organization_id", id),
      );
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/dashboard?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const getFederationDetails = async ({ args, signal }) => {
    const {
      start_date,
      end_date,
      keys,
      event_type,
      federation_id,
      destination_adserver_type,
      distinct_contributor_count,
    } = args || {};
    const params = new URLSearchParams({ start_date, end_date });
    if (keys?.length) params.append("keys", keys.join(","));
    if (event_type) params.append("event_type", event_type);
    if (federation_id) params.append("federation_id", federation_id);
    if (destination_adserver_type) {
      params.append("destination_adserver_type", destination_adserver_type);
    }
    if (distinct_contributor_count) {
      params.append("distinct_contributor_count", distinct_contributor_count);
    }
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/federation-month-level-count?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const getOrganizationDetails = async ({ args, signal }) => {
    const { start_date, end_date, keys, event_type, organization_id, federation_ids } =
      args || {};
    const params = new URLSearchParams({ start_date, end_date });
    if (keys?.length) params.append("keys", keys.join(","));
    if (event_type) params.append("event_type", event_type);
    if (organization_id) params.append("organization_id", organization_id);
    if (federation_ids?.length)
      params.append("filter_federation_ids", federation_ids.join(","));
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/organisation-month-level-count?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return normalizeOrganisationMonthResponse(await res.json());
  };

  const getAdserverDetails = async ({ args, signal }) => {
    const {
      start_date,
      end_date,
      keys,
      destination_adserver_type,
      distinct_contributor_count,
      federation_ids,
    } = args || {};
    const params = new URLSearchParams({ start_date, end_date });
    if (keys?.length) params.append("keys", keys.join(","));
    if (destination_adserver_type) {
      params.append("destination_adserver_type", destination_adserver_type);
    }
    if (distinct_contributor_count) {
      params.append("distinct_contributor_count", distinct_contributor_count);
    }
    if (federation_ids?.length)
      params.append("filter_federation_ids", federation_ids.join(","));
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/adserver-month-level-count?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const getEventDayLevelCount = async ({ args, signal }) => {
    const { event_type, start_date, end_date } = args || {};
    const params = new URLSearchParams({ start_date, end_date });
    if (event_type) params.append("event_type", event_type);
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/event-day-level-count?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const getFederationDayLevelCount = async ({ args, signal }) => {
    const {
      event_type,
      federation_id,
      destination_adserver_type,
      distinct_contributor_count,
      start_date,
      end_date,
    } = args || {};
    const params = new URLSearchParams({ start_date, end_date });
    if (event_type) params.append("event_type", event_type);
    if (federation_id) params.append("federation_id", federation_id);
    if (destination_adserver_type)
      params.append("destination_adserver_type", destination_adserver_type);
    if (distinct_contributor_count !== undefined)
      params.append(
        "distinct_contributor_count",
        String(distinct_contributor_count),
      );
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/federation-day-level-count?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const getOrganizationDayLevelCount = async ({ args, signal }) => {
    const { event_type, organization_id, start_date, end_date } = args || {};
    const params = new URLSearchParams({ start_date, end_date });
    if (event_type) params.append("event_type", event_type);
    if (organization_id) params.append("organization_id", organization_id);
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/organisation-day-level-count?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const getAdserverDayLevelCount = async ({ args, signal }) => {
    const {
      destination_adserver_type,
      distinct_contributor_count,
      start_date,
      end_date,
    } = args || {};
    const params = new URLSearchParams({ start_date, end_date });
    if (destination_adserver_type)
      params.append("destination_adserver_type", destination_adserver_type);
    if (distinct_contributor_count !== undefined)
      params.append(
        "distinct_contributor_count",
        String(distinct_contributor_count),
      );
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/adserver-day-level-count?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const getOrganizationActivationEnrichmentDayLevelCount = async ({
    args,
    signal,
  }) => {
    const { event_type, organization_id, start_date, end_date } = args || {};
    const params = new URLSearchParams({ start_date, end_date });
    if (event_type) params.append("event_type", event_type);
    if (organization_id) params.append("organization_id", organization_id);
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/organisation-activation-enrichment-day-level-count?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const tableGetEventDayLevelCount = async ({ args, signal }) => {
    const { event_type, start_date, end_date } = args || {};
    const params = new URLSearchParams({ start_date, end_date });
    if (event_type) params.append("event_type", event_type);
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/table-event-day-level-count?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const tableGetFederationMonthLevelCount = async ({ args, signal }) => {
    const {
      start_date,
      end_date,
      keys,
      event_type,
      federation_id,
      destination_adserver_type,
      distinct_contributor_count,
    } = args || {};
    const params = new URLSearchParams({ start_date, end_date });
    if (keys?.length) params.append("keys", keys.join(","));
    if (event_type) params.append("event_type", event_type);
    if (federation_id) params.append("federation_id", federation_id);
    if (destination_adserver_type)
      params.append("destination_adserver_type", destination_adserver_type);
    if (distinct_contributor_count)
      params.append("distinct_contributor_count", distinct_contributor_count);
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/table-federation-month-level-count?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const tableGetOrganizationMonthLevelCount = async ({ args, signal }) => {
    const { start_date, end_date, keys, event_type, organization_id, federation_ids } =
      args || {};
    const params = new URLSearchParams({ start_date, end_date });
    if (keys?.length) params.append("keys", keys.join(","));
    if (event_type) params.append("event_type", event_type);
    if (organization_id) params.append("organization_id", organization_id);
    if (federation_ids?.length)
      params.append("filter_federation_ids", federation_ids.join(","));
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/table-organisation-month-level-count?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const tableGetAdserverMonthLevelCount = async ({ args, signal }) => {
    const {
      start_date,
      end_date,
      keys,
      destination_adserver_type,
      distinct_contributor_count,
      federation_ids,
    } = args || {};
    const params = new URLSearchParams({ start_date, end_date });
    if (keys?.length) params.append("keys", keys.join(","));
    if (destination_adserver_type)
      params.append("destination_adserver_type", destination_adserver_type);
    if (distinct_contributor_count)
      params.append("distinct_contributor_count", distinct_contributor_count);
    if (federation_ids?.length)
      params.append("filter_federation_ids", federation_ids.join(","));
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/table-adserver-month-level-count?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const tableGetFederationDayLevelCount = async ({ args, signal }) => {
    const {
      event_type,
      federation_id,
      destination_adserver_type,
      distinct_contributor_count,
      start_date,
      end_date,
    } = args || {};
    const params = new URLSearchParams({ start_date, end_date });
    if (event_type) params.append("event_type", event_type);
    if (federation_id) params.append("federation_id", federation_id);
    if (destination_adserver_type)
      params.append("destination_adserver_type", destination_adserver_type);
    if (distinct_contributor_count !== undefined)
      params.append(
        "distinct_contributor_count",
        String(distinct_contributor_count),
      );
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/table-federation-day-level-count?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const tableGetOrganizationDayLevelCount = async ({ args, signal }) => {
    const { event_type, organization_id, start_date, end_date } = args || {};
    const params = new URLSearchParams({ start_date, end_date });
    if (event_type) params.append("event_type", event_type);
    if (organization_id) params.append("organization_id", organization_id);
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/table-organisation-day-level-count?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const tableGetAdserverDayLevelCount = async ({ args, signal }) => {
    const {
      destination_adserver_type,
      distinct_contributor_count,
      start_date,
      end_date,
    } = args || {};
    const params = new URLSearchParams({ start_date, end_date });
    if (destination_adserver_type)
      params.append("destination_adserver_type", destination_adserver_type);
    if (distinct_contributor_count !== undefined)
      params.append(
        "distinct_contributor_count",
        String(distinct_contributor_count),
      );
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/table-adserver-day-level-count?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const tableGetOrganizationActivationEnrichmentMonthLevelCount = async ({
    args,
    signal,
  }) => {
    const { event_type, organization_id, start_date, end_date, federation_ids } = args || {};
    const params = new URLSearchParams({ start_date, end_date });
    if (event_type) params.append("event_type", event_type);
    if (organization_id) params.append("organization_id", organization_id);
    if (federation_ids?.length)
      params.append("filter_federation_ids", federation_ids.join(","));
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/table-organisation-activation-enrichment-month-level-count?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const tableGetOrganizationActivationEnrichmentDayLevelCount = async ({
    args,
    signal,
  }) => {
    const { event_type, organization_id, start_date, end_date, federation_ids } = args || {};
    const params = new URLSearchParams({ start_date, end_date });
    if (event_type) params.append("event_type", event_type);
    if (organization_id) params.append("organization_id", organization_id);
    if (federation_ids?.length)
      params.append("filter_federation_ids", federation_ids.join(","));
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/table-organisation-activation-enrichment-day-level-count?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  // ── Organization Dashboard API functions ─────────────────────────────────────
  // All routes use the org-scoped base: /orgs/{org_id}/...
  // org_id is resolved from args.org_id first, then localStorage("organizationkey").
  const resolveOrgId = (args: any): string =>
    args?.org_id || localStorage.getItem("organizationkey") || "";

  const orgGetDashboard = async ({ args, signal }) => {
    const { start_date, end_date, keys, from_organization } = args || {};
    const orgId = resolveOrgId(args);
    const params = new URLSearchParams({ start_date, end_date });
    params.append("organization_id", orgId);
    if (keys?.length) params.append("keys", keys.join(","));
    if (from_organization !== undefined)
      params.append("from_organization", from_organization);
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/orgs/${orgId}/dashboard?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const orgGetFederationDetails = async ({ args, signal }) => {
    const {
      start_date,
      end_date,
      event_type,
      federation_id,
      destination_adserver_type,
      distinct_contributor_count,
      from_organization,
    } = args || {};
    const orgId = resolveOrgId(args);
    const params = new URLSearchParams({ start_date, end_date });
    params.append("organization_id", orgId);
    if (event_type) params.append("event_type", event_type);
    if (federation_id) params.append("federation_id", federation_id);
    if (destination_adserver_type) {
      params.append("destination_adserver_type", destination_adserver_type);
    }
    if (distinct_contributor_count) {
      params.append("distinct_contributor_count", distinct_contributor_count);
    }
    if (from_organization !== undefined)
      params.append("from_organization", from_organization);
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/orgs/${orgId}/federations/monthly?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const orgGetOrganizationDetails = async ({ args, signal }) => {
    const { start_date, end_date, event_type, organization_id } = args || {};
    const orgId = resolveOrgId(args);
    const params = new URLSearchParams({ start_date, end_date });
    if (event_type) params.append("event_type", event_type);
    // organization_id doubles as the org-scoped data filter (explicit selection,
    // e.g. an admin narrowing to one org) and the OrganizationGuard's required
    // param — fall back to the caller's own org when no filter is selected.
    params.append("organization_id", organization_id || orgId);
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/organisation-month-level-count?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return normalizeOrganisationMonthResponse(await res.json());
  };

  const orgGetAdserverDetails = async ({ args, signal }) => {
    const {
      start_date,
      end_date,
      destination_adserver_type,
      distinct_contributor_count,
      from_organization,
    } = args || {};
    const orgId = resolveOrgId(args);
    const params = new URLSearchParams({ start_date, end_date });
    params.append("organization_id", orgId);
    if (destination_adserver_type) {
      params.append("destination_adserver_type", destination_adserver_type);
    }
    if (distinct_contributor_count) {
      params.append("distinct_contributor_count", distinct_contributor_count);
    }
    if (from_organization !== undefined)
      params.append("from_organization", from_organization);
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/orgs/${orgId}/adservers/monthly?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const orgGetContributorCountValues = async ({ args, signal }) => {
    const { start_date, end_date } = args || {};
    const params = new URLSearchParams({ start_date, end_date });
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/contributor-count-values?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const orgTableGetFederationMonthLevelCount = async ({ args, signal }) => {
    const {
      start_date,
      end_date,
      event_type,
      federation_id,
      destination_adserver_type,
      distinct_contributor_count,
      from_organization,
    } = args || {};
    const orgId = resolveOrgId(args);
    const params = new URLSearchParams({ start_date, end_date, view: "table" });
    params.append("organization_id", orgId);
    if (event_type) params.append("event_type", event_type);
    if (federation_id) params.append("federation_id", federation_id);
    if (destination_adserver_type)
      params.append("destination_adserver_type", destination_adserver_type);
    if (distinct_contributor_count)
      params.append("distinct_contributor_count", distinct_contributor_count);
    if (from_organization !== undefined)
      params.append("from_organization", from_organization);
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/orgs/${orgId}/federations/monthly?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const orgTableGetOrganizationMonthLevelCount = async ({ args, signal }) => {
    const { start_date, end_date, event_type, organization_id } = args || {};
    const orgId = resolveOrgId(args);
    const params = new URLSearchParams({ start_date, end_date });
    if (event_type) params.append("event_type", event_type);
    // organization_id doubles as the org-scoped data filter (explicit selection,
    // e.g. an admin narrowing to one org) and the OrganizationGuard's required
    // param — fall back to the caller's own org when no filter is selected.
    params.append("organization_id", organization_id || orgId);
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/table-organisation-month-level-count?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const orgTableGetAdserverMonthLevelCount = async ({ args, signal }) => {
    const {
      start_date,
      end_date,
      destination_adserver_type,
      distinct_contributor_count,
      from_organization,
    } = args || {};
    const orgId = resolveOrgId(args);
    const params = new URLSearchParams({ start_date, end_date, view: "table" });
    params.append("organization_id", orgId);
    if (destination_adserver_type)
      params.append("destination_adserver_type", destination_adserver_type);
    if (distinct_contributor_count)
      params.append("distinct_contributor_count", distinct_contributor_count);
    if (from_organization !== undefined)
      params.append("from_organization", from_organization);
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/orgs/${orgId}/adservers/monthly?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const orgTableGetFederationDayLevelCount = async ({ args, signal }) => {
    const {
      event_type,
      federation_id,
      destination_adserver_type,
      distinct_contributor_count,
      start_date,
      end_date,
      from_organization,
    } = args || {};
    const orgId = resolveOrgId(args);
    const params = new URLSearchParams({ start_date, end_date, view: "table" });
    params.append("organization_id", orgId);
    if (event_type) params.append("event_type", event_type);
    if (federation_id) params.append("federation_id", federation_id);
    if (destination_adserver_type)
      params.append("destination_adserver_type", destination_adserver_type);
    if (distinct_contributor_count !== undefined)
      params.append(
        "distinct_contributor_count",
        String(distinct_contributor_count),
      );
    if (from_organization !== undefined)
      params.append("from_organization", from_organization);
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/orgs/${orgId}/federations/daily?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const orgTableGetOrganizationDayLevelCount = async ({ args, signal }) => {
    const { event_type, organization_id, start_date, end_date } = args || {};
    const orgId = resolveOrgId(args);
    const params = new URLSearchParams({ start_date, end_date });
    if (event_type) params.append("event_type", event_type);
    // organization_id doubles as the org-scoped data filter (explicit selection,
    // e.g. an admin narrowing to one org) and the OrganizationGuard's required
    // param — fall back to the caller's own org when no filter is selected.
    params.append("organization_id", organization_id || orgId);
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/table-organisation-day-level-count?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const orgTableGetAdserverDayLevelCount = async ({ args, signal }) => {
    const {
      destination_adserver_type,
      distinct_contributor_count,
      start_date,
      end_date,
      from_organization,
    } = args || {};
    const orgId = resolveOrgId(args);
    const params = new URLSearchParams({ start_date, end_date, view: "table" });
    params.append("organization_id", orgId);
    if (destination_adserver_type)
      params.append("destination_adserver_type", destination_adserver_type);
    if (distinct_contributor_count !== undefined)
      params.append(
        "distinct_contributor_count",
        String(distinct_contributor_count),
      );
    if (from_organization !== undefined)
      params.append("from_organization", from_organization);
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/orgs/${orgId}/adservers/daily?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const orgTableGetOrganizationActivationEnrichmentMonthLevelCount = async ({
    args,
    signal,
  }) => {
    const { start_date, end_date, activation_enrichment, from_organization } =
      args || {};
    const orgId = resolveOrgId(args);
    const params = new URLSearchParams({ start_date, end_date, view: "table" });
    params.append("organization_id", orgId);
    if (activation_enrichment)
      params.append("activation_enrichment", activation_enrichment);
    if (from_organization !== undefined)
      params.append("from_organization", from_organization);
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/orgs/${orgId}/activation-enrichment/monthly?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const orgTableGetOrganizationActivationEnrichmentDayLevelCount = async ({
    args,
    signal,
  }) => {
    const { start_date, end_date, activation_enrichment, from_organization } =
      args || {};
    const orgId = resolveOrgId(args);
    const params = new URLSearchParams({ start_date, end_date, view: "table" });
    params.append("organization_id", orgId);
    if (activation_enrichment)
      params.append("activation_enrichment", activation_enrichment);
    if (from_organization !== undefined)
      params.append("from_organization", from_organization);
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/orgs/${orgId}/activation-enrichment/daily?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const orgGetEventDayLevelCount = async ({ args, signal }) => {
    const { event_type, start_date, end_date, from_organization } = args || {};
    const orgId = resolveOrgId(args);
    const params = new URLSearchParams({ start_date, end_date });
    params.append("organization_id", orgId);
    if (event_type) params.append("event_type", event_type);
    if (from_organization !== undefined)
      params.append("from_organization", from_organization);
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/orgs/${orgId}/events/daily?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const orgGetFederationDayLevelCount = async ({ args, signal }) => {
    const {
      event_type,
      federation_id,
      destination_adserver_type,
      distinct_contributor_count,
      start_date,
      end_date,
      from_organization,
    } = args || {};
    const orgId = resolveOrgId(args);
    const params = new URLSearchParams({ start_date, end_date });
    params.append("organization_id", orgId);
    if (event_type) params.append("event_type", event_type);
    if (federation_id) params.append("federation_id", federation_id);
    if (destination_adserver_type)
      params.append("destination_adserver_type", destination_adserver_type);
    if (distinct_contributor_count !== undefined)
      params.append(
        "distinct_contributor_count",
        String(distinct_contributor_count),
      );
    if (from_organization !== undefined)
      params.append("from_organization", from_organization);
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/orgs/${orgId}/federations/daily?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const orgGetOrganizationDayLevelCount = async ({ args, signal }) => {
    const { event_type, organization_id, start_date, end_date } = args || {};
    const orgId = resolveOrgId(args);
    const params = new URLSearchParams({ start_date, end_date });
    if (event_type) params.append("event_type", event_type);
    // organization_id doubles as the org-scoped data filter (explicit selection,
    // e.g. an admin narrowing to one org) and the OrganizationGuard's required
    // param — fall back to the caller's own org when no filter is selected.
    params.append("organization_id", organization_id || orgId);
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/organisation-day-level-count?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const orgGetAdserverDayLevelCount = async ({ args, signal }) => {
    const {
      destination_adserver_type,
      distinct_contributor_count,
      start_date,
      end_date,
      from_organization,
    } = args || {};
    const orgId = resolveOrgId(args);
    const params = new URLSearchParams({ start_date, end_date });
    params.append("organization_id", orgId);
    if (destination_adserver_type)
      params.append("destination_adserver_type", destination_adserver_type);
    if (distinct_contributor_count !== undefined)
      params.append(
        "distinct_contributor_count",
        String(distinct_contributor_count),
      );
    if (from_organization !== undefined)
      params.append("from_organization", from_organization);
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/orgs/${orgId}/adservers/daily?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const orgTableGetEventDayLevelCount = async ({ args, signal }) => {
    const { event_type, start_date, end_date, from_organization } = args || {};
    const orgId = resolveOrgId(args);
    const params = new URLSearchParams({ start_date, end_date, view: "table" });
    params.append("organization_id", orgId);
    if (event_type) params.append("event_type", event_type);
    if (from_organization !== undefined)
      params.append("from_organization", from_organization);
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/dashboard/orgs/${orgId}/events/daily?${params}`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  // ── End Organization Dashboard API functions ─────────────────────────────────

  const getActivationHistoryExport = async ({ args }) => {
    const {
      federationId,
      organizationId,
      page_size,
      scan_index_forward,
      last_key,
      Identifier,
      query_id,
      start_date,
      target,
      role,
      campaign_name,
      file_format,
    } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query/marked/activation?` +
      new URLSearchParams({
        federation_id: federationId,
        organization_id: organizationId,
      }) +
      `&page_size=${page_size}` +
      `&start_date=${start_date}` +
      `&last_key=${last_key && last_key !== "undefined" ? new Date(last_key).toISOString().replace(/\.\d+Z$/, "Z") : last_key}` +
      `&identifier=${Identifier}` +
      `&query_id=${query_id}` +
      `&campaign_name=${campaign_name}` +
      `&target=${target}` +
      `&scan_index_forward=${scan_index_forward}` +
      `&role=${role}` +
      `&file_format=${file_format}`;

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    if (file_format === "csv" || file_format === "pdf") {
      return res;
    }

    return await res.json();
  };

  const getCampaignReportsCSV = async ({ args }) => {
    const {
      federationId,
      organizationId,
      page_size,
      scan_index_forward,
      Identifier,
      contributor_org_id,
      query_id,
      start_date,
      last_key,
      role,
      campaign_name,
      file_format,
    } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query?` +
      new URLSearchParams({
        federation_id: federationId,
        organization_id: organizationId,
      }) +
      `&identifier=${Identifier}` +
      `&contributor_org_id=${contributor_org_id}` +
      `&query_id=${query_id}` +
      `&campaign_name=${campaign_name}` +
      `&start_date=${start_date}` +
      `&last_key=${last_key && last_key !== "undefined" ? new Date(last_key).toISOString().replace(/\.\d+Z$/, "Z") : last_key}` +
      `&page_size=${page_size}` +
      `&scan_index_forward=${scan_index_forward}` +
      `&role=${role}` +
      `&file_format=${file_format}`;

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      const errText = await res.text();
      throw new Error(`Failed to download: ${res.status} | ${errText}`);
    }

    return res;
  };

  const getProvenanceTrailPDF = async ({ args }) => {
    const {
      federation_id,
      offset,
      limit,
      sort = "desc",
      filterOrgId,
      organization_id,
      event,
      query_id,
      start_date,
      end_date,
      user_name,
      role,
      fromOrganization,
    } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/index/getpdf?` +
      new URLSearchParams({
        federation_id,
        offset,
        limit,
        sort,
        organization_id,
        event,
        query_id,
        user_name,
        start_date,
        end_date,
        role,
        fromOrganization,
      }) +
      `&filter_organization_id=${filterOrgId}`;

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      const errText = await res.text();
      throw new Error(`Failed to download: ${res.status} | ${errText}`);
    }

    return res;
  };

  const getProvenanceTrailCSV = async ({ args }) => {
    const {
      federation_id,
      offset,
      limit,
      sort = "desc",
      filterOrgId,
      organization_id,
      event,
      query_id,
      start_date,
      end_date,
      user_name,
      role,
      fromOrganization,
    } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/index/getcsv?` +
      new URLSearchParams({
        federation_id,
        offset,
        limit,
        sort,
        organization_id,
        event,
        query_id,
        user_name,
        start_date,
        end_date,
        role,
        fromOrganization,
      }) +
      `&filter_organization_id=${filterOrgId}`;

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      const errText = await res.text();
      throw new Error(`Failed to download: ${res.status} | ${errText}`);
    }

    return res;
  };

  // const getAttributeOptions = async ({ args, signal }) => {
  //   const { attribute } = args;
  //   const res = await fetch(`${FQS_API_URL}/api/v1/attributes/${attribute}`, {
  //     method: "GET",
  //     headers: await getHeaders(),
  //     signal,
  //   });
  //   if (!res.ok) {
  //     handleError(res);
  //   }
  //   const data = await res.json();
  //   return data;
  // };
  const getFederations = async ({
    args,
  }: {
    args?: {
      page_size?: number;
      cursor?: string;
      prev_cursor?: string;
      name?: string;
    };
  } = {}) => {
    const params = new URLSearchParams({ include_deleted: String(false) });
    if (args?.page_size) params.append("page_size", String(args.page_size));
    if (args?.cursor) params.append("cursor", args.cursor);
    if (args?.prev_cursor) params.append("prev_cursor", args.prev_cursor);
    if (args?.name) params.append("name", args.name);
    const url = `${Atom_proxy}/api/v1/spa/registry/federations?${params}`;
    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });
    if (!res.ok) {
      handleError(res);
    }
    const data = await res.json();
    return data;
  };
  const getFederationsByOrgID = async ({ args }) => {
    const { organizationID } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/registry/federationmembers/organizations?` +
      new URLSearchParams({
        organization_id: organizationID,
      });

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const getCampaignNames = async ({ args }) => {
    const { orgId } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/campaigns?` +
      new URLSearchParams({
        organization_id: orgId,
      });

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  // Categories a query can be published under as a cohort. Only live,
  // non-deleted ones are offered; both filters are always sent together.
  // orgId and fedId each narrow the list when given; the ONAM marketplace
  // passes only fedId, since its categories belong to the federation's
  // publishers, not to the organisation that is browsing.
  const getCategories = async ({ args }) => {
    const { orgId, fedId } = args;

    const params = new URLSearchParams({
      is_live: "true",
      is_deleted: "false",
    });
    if (orgId) {
      params.set("organization_id", orgId);
    }
    if (fedId) {
      params.set("federation_id", fedId);
    }

    const url = `${Atom_proxy}/api/v1/spa/fqs/categories?` + params;

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  // Every organisation's published queries in a federation (FQS GET /query/published/federation),
  // for the federation's members to browse. The caller's organisation must belong to the federation
  // unless the role is the super user, which needs no organisation. Not paginated.
  const getFederationPublished = async ({ args }) => {
    const { federationId, organizationId, role } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query/published/federation?` +
      new URLSearchParams({
        federation_id: federationId,
        ...(role ? { role } : {}),
        ...(organizationId ? { organization_id: organizationId } : {}),
      });

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      return handleAndParseError(res);
    }

    return await res.json();
  };

  // Counts of a federation's published queries and categories (FQS GET /query/published/federation/summary):
  // { federation_id, published_query_count, category_count }. Same access rule as getFederationPublished.
  const getFederationSummary = async ({ args }) => {
    const { federationId, organizationId, role } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query/published/federation/summary?` +
      new URLSearchParams({
        federation_id: federationId,
        ...(role ? { role } : {}),
        ...(organizationId ? { organization_id: organizationId } : {}),
      });

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      return handleAndParseError(res);
    }

    return await res.json();
  };

  const getApplication = async () => {
    const str = JSON.stringify(tenants);
    const match = str.match(/"application_id"\s*:\s*"([^"]+)"/);
    const nameMatch = str.match(/"application_name"\s*:\s*"([^"]+)"/);
    const orgNameMatch = str.match(/"organization_name"\s*:\s*"([^"]+)"/);

    const applicationId = match ? match[1] : null;
    const applicationName = nameMatch ? nameMatch[1] : null;
    const organizationName = orgNameMatch ? orgNameMatch[1] : null;

    if (!applicationId) {
      throw new Error("application_id not found in tenants object");
    }

    const url =
      `${Atom_proxy}/api/v1/spa/registry/applicationsetting?` +
      new URLSearchParams({ application_id: applicationId });
    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });
    if (!res.ok) {
      if (res.status === 403) {
        setRbacAccessDenied(true);
        throw new Error("RBAC_ACCESS_DENIED");
      }
      try {
        const errBody = await res.json();
        if (
          errBody?.error ===
          "organization does not have access to the specified application"
        ) {
          const orgId = localStorage.getItem("organizationkey") || "";
          throw new Error(
            `ORG_APP_MISMATCH:${JSON.stringify({ orgId, organizationName, applicationId, applicationName })}`,
          );
        }
      } catch (parseErr) {
        if ((parseErr as Error)?.message?.startsWith("ORG_APP_MISMATCH:"))
          throw parseErr;
      }
      handleError(res);
    }
    const data = await res.json();
    return data;
  };

  const getAllApplications = async ({
    args = {},
    signal,
  }: { args?: Record<string, any>; signal?: AbortSignal } = {}) => {
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/registry/applicationsettings`,
      {
        method: "GET",
        headers: {
          ...(await getHeaders()),
          Accept: "application/json",
        },
        signal,
      },
    );
    if (!res.ok) handleError(res);
    return res.json();
  };

  const getFederationsByApplicationIds = async ({
    args,
    signal,
  }: {
    args: { application_ids: string[] };
    signal?: AbortSignal;
  }) => {
    const { application_ids } = args;
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/registry/applicationsettings/federations-organizations`,
      {
        method: "POST",
        headers: {
          ...(await getHeaders()),
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ application_ids }),
        signal,
      },
    );
    if (!res.ok) return handleAndParseError(res);
    return res.json();
  };

  const getConfig = async ({ args }) => {
    const { organizationId } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/getconfig?` +
      new URLSearchParams({
        organization_id: organizationId,
        client: "application",
      });

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const getFednDetails = async ({ args }) => {
    const { federationID } = args;

    if (!federationID) return null;

    const url =
      `${Atom_proxy}/api/v1/spa/registry/federation?` +
      new URLSearchParams({
        federation_id: federationID,
      });

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const getOrgByFederationID = async ({ args }) => {
    const { federationID } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/registry/federationmembers/federations?` +
      new URLSearchParams({
        federation_id: federationID,
      });

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const getIndexOrganizations = async ({ args }) => {
    const { federation_id, organization_id } = args;
    const url =
      `${Atom_proxy}/api/v1/spa/index/getorganizations?` +
      new URLSearchParams({ federation_id, organization_id });
    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });
    if (!res.ok) {
      handleError(res);
    }
    return await res.json();
  };

  const getLogoFederationID = async ({
    args,
  }: {
    args: {
      organizationID: string;
      name?: string;
      page_size?: number;
      cursor?: string;
    };
  }) => {
    const { organizationID, name, page_size, cursor } = args;
    const params = new URLSearchParams({ organization_id: organizationID });
    if (page_size) params.append("page_size", String(page_size));
    if (name) params.append("name", name);
    if (cursor) params.append("cursor", cursor);

    const url = `${Atom_proxy}/api/v1/spa/registry/federationmembers/organizations/logo?${params}`;

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const getRoles = async () => {
    const url = `${Atom_proxy}/api/v1/spa/registry/roles`;
    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });
    if (!res.ok) {
      handleError(res);
    }
    const data = await res.json();
    return data;
  };
  const putDatasource = async ({ args }) => {
    const { body, reLoad } = args;
    const res = await fetch(`${Atom_proxy}/api/v1/spa/aqs/data-sources`, {
      method: "POST",
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      handleError(res);
    }

    if (reLoad !== undefined) {
      reLoad();
    }

    const data = await res.json();
    return data;
  };

  const putEngage = async ({ args }) => {
    const { body, role, organizationId, applicationId: appId } = args;
    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query/engage?` +
      new URLSearchParams({
        role,
        organization_id: organizationId,
        ...(appId ? { application_id: appId } : {}),
      });
    const res = await fetch(url, {
      method: "POST",
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      if (res.status === 401) clearAuth();
      const rawText = await res.text();
      let errorMessage = rawText || res.statusText || `API Error ${res.status}`;
      try {
        const errorBody = JSON.parse(rawText);
        if (errorBody?.message) errorMessage = errorBody.message;
        else if (errorBody?.error) errorMessage = errorBody.error;
      } catch {}
      const error: any = new Error(errorMessage);
      // Engagement limit reached / no connectors enabled for this query
      // type both come back as 400s from enforceActivationLimits; carry
      // the status the same way putquery does so callers can distinguish
      // that from a hard 5xx infra failure if they ever need to.
      error.status = res.status;
      throw error;
    }

    const data = await res.json();
    return data;
  };

  const putConnector = async ({ args }) => {
    const { body, reLoad } = args;
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/aqs/data-sources/connector`,
      {
        method: "POST",
        headers: await getHeaders(),
        body: JSON.stringify(body),
      },
    );
    if (!res.ok) {
      handleError(res);
    }

    if (reLoad !== undefined) {
      reLoad();
    }

    const data = await res.json();
    return data;
  };
  const getSchema = async ({ args }) => {
    const { federationId, organizationId } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/buckets?` +
      new URLSearchParams({
        federation_id: federationId,
        organization_id: organizationId,
      });

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const getDataByQueryId = async ({ args }) => {
    const { queryId, organizationId } = args;
    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query/${queryId}?` +
      new URLSearchParams({ organization_id: organizationId });
    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });
    if (!res.ok) {
      handleError(res);
    }
    const data = await res.json();
    return data;
  };
  const getAttributes = async ({ args }) => {
    const {
      federationId,
      organizationId,
      identifier,
      canonical_identity_type,
      buckets,
    } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/filterattributes?` +
      new URLSearchParams({
        federation_id: federationId,
        organization_id: organizationId,
      }) +
      `&identifier=${identifier}` +
      `&canonical_identity_type=${canonical_identity_type}` +
      `&bucket_ids=${buckets}`;

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const getSelectAttributes = async ({ args }) => {
    const { federationId, identifier, buckets } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/selectattributes?` +
      new URLSearchParams({
        federation_id: federationId,
      }) +
      `&identifier=${identifier}` +
      `&bucket_ids=${buckets}`;

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const getAttrDistribution = async ({ args }) => {
    const { federationId, organizationId } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/distributioncount/filters?` +
      new URLSearchParams({
        federation_id: federationId,
        organization_id: organizationId,
      });

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    const text = await res.text();
    let body: any = {};
    try {
      body = text ? JSON.parse(text) : {};
    } catch {}

    if (!res.ok) {
      const message = body?.message || `Error ${res.status}`;
      if (res.status === 401) clearAuth();
      throw new Error(message);
    }

    return body;
  };

  const putmatchRate = async ({ args }) => {
    const { federationId, organizationId, body } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/matchrate?` +
      new URLSearchParams({
        federation_id: federationId,
        organization_id: organizationId,
      });

    const res = await fetch(url, {
      method: "POST",
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const getmatchrates = async ({ args }) => {
    const { federationId, organizationId } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/matchrate?` +
      new URLSearchParams({
        federation_id: federationId,
        organization_id: organizationId,
      });

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const getmatchratequery = async ({ args }) => {
    const {
      federationId,
      organizationId,
      page_size,
      scan_index_forward,
      last_key,
      Identifier,
      query_id,
      start_date,
      target,
      role,
      campaign_name,
    } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query/marked/activation?` +
      new URLSearchParams({
        federation_id: federationId,
        organization_id: organizationId,
      }) +
      `&page_size=${page_size}` +
      `&start_date=${start_date}` +
      `&last_key=${last_key && last_key !== "undefined" ? new Date(last_key).toISOString().replace(/\.\d+Z$/, "Z") : last_key}` +
      `&identifier=${Identifier}` +
      `&query_id=${query_id}` +
      `&campaign_name=${campaign_name}` +
      `&target=${target}` +
      `&scan_index_forward=${scan_index_forward}` +
      `&role=${role}`;

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const getactivationconnectorsforadmin = async ({ args }) => {
    const { organizationId } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/registry/federationmembers/organizations/activation/connectors?` +
      new URLSearchParams({
        organization_id: organizationId,
      }) +
      `&role=${config.superUserRole}`;

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const putactivationconnectorsforadmin = async ({ args }) => {
    const { organizationId, body } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/registry/federationmembers/organizations/activation/connectors?` +
      new URLSearchParams({
        organization_id: organizationId,
      }) +
      `&role=${config.superUserRole}`;

    const res = await fetch(url, {
      method: "PATCH",
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const getbucketById = async ({ args }) => {
    const { userBucketId, organizationId } = args;
    const url =
      `${Atom_proxy}/api/v1/spa/fqs/buckets/${userBucketId}?` +
      new URLSearchParams({ organization_id: organizationId });
    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });
    if (!res.ok) {
      handleError(res);
    }
    const data = await res.json();
    return data;
  };

  const getTablesByDataSourceId = async ({ args, signal }) => {
    const { datasourceId, organizationId, onRetry } = args;
    const url =
      `${Atom_proxy}/api/v1/spa/aqs/data-sources/${datasourceId}/tables?` +
      new URLSearchParams({
        organization_id: organizationId,
      });

    for (let attempt = 0; attempt <= TABLES_LIST_MAX_RETRIES; attempt++) {
      const res = await fetch(url, {
        method: "GET",
        headers: await getHeaders(),
        signal,
      });

      if (res.ok) {
        return await res.json();
      }

      // Branch on status. 503 is transient; do not inspect `detail` text.
      if (res.status === 503 && attempt < TABLES_LIST_MAX_RETRIES) {
        const retryAfterSeconds =
          Number(res.headers.get("Retry-After")) ||
          TABLES_LIST_DEFAULT_RETRY_AFTER_SECONDS;
        onRetry?.();
        await waitWithAbort(retryAfterSeconds * 1000, signal);
        continue;
      }

      let errorMessage = GENERIC_ERROR_MESSAGE;
      try {
        const errorData = await res.json();
        errorMessage = errorData?.message || errorData?.error || errorMessage;
      } catch {
        // response body is not JSON, fall back to generic
      }
      throw new Error(errorMessage);
    }
  };

  const getDatasourceById = async ({ args }) => {
    const { datasourceId, organizationId } = args;
    const url =
      `${Atom_proxy}/api/v1/spa/aqs/data-sources/${datasourceId}` +
      new URLSearchParams({
        organization_id: organizationId,
      });
    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });
    if (!res.ok) {
      handleError(res);
    }
    const data = await res.json();
    return data;
  };
  const getorganizationById = async ({ args }) => {
    const { organizationID } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/registry/organization?` +
      new URLSearchParams({
        organization_id: organizationID,
      });

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const getAdserversByOrgId = async ({ args }) => {
    const { organizationId } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/registry/adserver?` +
      new URLSearchParams({
        organization_id: organizationId,
      });

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const deleteAdserverByOrgId = async ({ args }) => {
    const { organizationId, uuids } = args;

    const res = await fetch(`${Atom_proxy}/api/v1/spa/registry/adserver`, {
      method: "DELETE",
      headers: await getHeaders(),
      body: JSON.stringify({ organization_id: organizationId, uuids }),
    });

    if (!res.ok) {
      handleError(res);
    }

    const text = await res.text();
    return text ? JSON.parse(text) : {};
  };

  const postAdserverByOrgId = async ({ args }) => {
    const { organizationId, adServer } = args;

    const res = await fetch(`${Atom_proxy}/api/v1/spa/registry/adserver`, {
      method: "POST",
      headers: await getHeaders(),
      body: JSON.stringify({
        organization_id: organizationId,
        ad_server: adServer,
      }),
    });

    if (!res.ok) {
      if (res.status === 401) clearAuth();
      const body = await res.text().catch(() => "");
      if (body) {
        try {
          const parsed = JSON.parse(body);
          throw new Error(parsed.error || parsed.message || body);
        } catch (e) {
          if (!(e instanceof SyntaxError)) throw e;
        }
        throw new Error(body);
      }
      throw new Error(`Error ${res.status}`);
    }

    return await res.json();
  };

  const updateOrganizationById = async ({ args }) => {
    const { organizationID, body } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/registry/organizations?` +
      new URLSearchParams({
        organization_id: organizationID,
      });

    const res = await fetch(url, {
      method: "PATCH",
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      return handleAndParseError(res);
    }

    return await res.json();
  };

  const updatequery = async ({ args }) => {
    const { body, QueryId, reloadQueries, organizationId } = args;
    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query/${QueryId}?` +
      new URLSearchParams({
        operation: "mark",
        organization_id: organizationId,
      });
    const res = await fetch(url, {
      method: "PATCH",
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      handleError(res);
    }
    if (reloadQueries !== undefined) {
      reloadQueries();
    }

    const data = await res.json();
    return data;
  };

  const deletequery = async ({ args }) => {
    const { QueryId, organizationId } = args;
    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query/${QueryId}?` +
      new URLSearchParams({ organization_id: organizationId });
    const res = await fetch(url, {
      method: "DELETE",
      headers: await getHeaders(),
      // body: JSON.stringify(body),
    });
    if (!res.ok) {
      handleError(res);
    }
    const data = await res.json();
    return data;
  };

  const patchQueryCohort = async ({ args }) => {
    const { queryId, body, organizationId } = args;
    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query/${queryId}/category?` +
      new URLSearchParams({ organization_id: organizationId });
    const res = await fetch(url, {
      method: "PATCH",
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });
    // FQS says why it refused (no campaign, category not found, empty result...), so keep its message
    if (!res.ok) {
      return handleAndParseError(res);
    }
    const data = await res.json();
    return data;
  };

  // Changes the CPM of a published query and nothing else (FQS PATCH /query/{id}/cpm), so the category
  // is not re-sent or re-validated. FQS answers 409 for a query that is not published, with its reason.
  const patchQueryCpm = async ({ args }) => {
    const { queryId, cpm, organizationId } = args;
    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query/${queryId}/cpm?` +
      new URLSearchParams({ organization_id: organizationId });
    const res = await fetch(url, {
      method: "PATCH",
      headers: await getHeaders(),
      body: JSON.stringify({ cpm }),
    });
    if (!res.ok) {
      return handleAndParseError(res);
    }
    return await res.json();
  };

  // Reruns a completed query's stored definition as a new query (FQS POST /query/{id}/rerun).
  // Answers 201 with the new query, which starts out RUNNING.
  const rerunQuery = async ({ args }) => {
    const { queryId, organizationId, role, applicationId, body } = args;
    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query/${queryId}/rerun?` +
      new URLSearchParams({
        organization_id: organizationId,
        ...(role ? { role } : {}),
        ...(applicationId ? { application_id: applicationId } : {}),
      });
    const res = await fetch(url, {
      method: "POST",
      headers: await getHeaders(),
      body: JSON.stringify(body || {}),
    });
    if (!res.ok) {
      // FQS's error is { step, message, error } and handleAndParseError keeps only the message. The caller needs the
      // status and step to tell "a rerun of this query is already running" (409, rerun_tracking) from the other refusals.
      const step = await res.clone().json().then((b) => b?.step).catch(() => undefined);
      try {
        return await handleAndParseError(res);
      } catch (e) {
        e.status = res.status;
        e.step = step;
        throw e;
      }
    }
    return await res.json();
  };

  const transferFiles = async ({ args }) => {
    const { body, reloadQueries, organizationId } = args;
    const url =
      `${Atom_proxy}/api/v1/spa/fqs/audiencegeneration/transferfiles?` +
      new URLSearchParams({ organization_id: organizationId });
    const res = await fetch(url, {
      method: "PATCH",
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      handleError(res);
    }
    if (reloadQueries !== undefined) {
      reloadQueries();
    }

    const data = await res.json();
    return data;
  };

  const getDatasource = async ({ args }) => {
    const res = await fetch(`${Atom_proxy}/api/v1/spa/aqs/data-sources`, {
      method: "GET",
      headers: await getHeaders(),
    });
    if (!res.ok) {
      handleError(res);
    }
    const data = await res.json();
    return data;
  };
  const getdatasourceStatus = async ({ args }) => {
    const { datasourceId } = args;
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/aqs/data-sources/status/${datasourceId}`,
      {
        method: "GET",
        headers: await getHeaders(),
      },
    );
    if (!res.ok) {
      handleError(res);
    }
    const data = await res.json();
    return data;
  };
  const getTablesByName = async ({ args }) => {
    const { tableName, datasourceId, organizationId } = args;
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/aqs/data-sources/${datasourceId}/tables/${tableName}?` +
        new URLSearchParams({
          organization_id: organizationId,
        }),
      {
        method: "GET",
        headers: await getHeaders(),
      },
    );
    if (!res.ok) {
      handleError(res);
    }
    const data = await res.json();
    return data;
  };
  const merkleProofapi = async ({ args }) => {
    const { HashId } = args;
    const res = await fetch(
      `${Atom_proxy}/api/v1/spa/merkelproof/queryByHashPolygon/${HashId}`,
      {
        method: "GET",
        headers: await getHeaders(),
      },
    );
    if (!res.ok) {
      handleError(res);
    }
    const data = await res.json();
    return data;
  };
  const getDatasourceByFedId = async ({ args }) => {
    const { organizationId } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/aqs/data-sources?` +
      new URLSearchParams({
        organization_id: organizationId,
      });

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const getAudienceGeneration = async ({ args }) => {
    const { queryId, user_name, sessionOrganizationId } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/audiencegeneration?` +
      new URLSearchParams({
        organization_id: sessionOrganizationId,
      }) +
      `&query_job_id=${queryId}` +
      `&user_name=${user_name}`;

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const getOrganizations = async ({
    args,
  }: {
    args?: { name?: string; page_size?: number; cursor?: string };
  } = {}) => {
    const params = new URLSearchParams({ include_deleted: String(false) });
    params.append("page_size", String(args?.page_size ?? 50));
    if (args?.name) params.append("name", args.name);
    if (args?.cursor) params.append("cursor", args.cursor);
    const url = `${Atom_proxy}/api/v1/spa/registry/organizations?${params}`;
    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });
    if (!res.ok) {
      handleError(res);
    }
    const data = await res.json();
    return data;
  };
  const getquery = async ({ args }) => {
    const {
      federationId,
      organizationId,
      page_size,
      scan_index_forward,
      Identifier,
      contributor_org_id,
      query_id,
      start_date,
      last_key,
      role,
      campaign_name,
      nl_query = false,
      include_child_queries = true,
      exclude_zero_count,
      exclude_failed,
    } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query?` +
      new URLSearchParams({
        federation_id: federationId,
        organization_id: organizationId,
      }) +
      `&identifier=${Identifier}` +
      `&contributor_org_id=${contributor_org_id}` +
      `&query_id=${query_id}` +
      `&campaign_name=${campaign_name}` +
      `&start_date=${start_date}` +
      `&last_key=${last_key && last_key !== "undefined" ? new Date(last_key).toISOString().replace(/\.\d+Z$/, "Z") : last_key}` +
      `&page_size=${page_size}` +
      `&scan_index_forward=${scan_index_forward}` +
      `&role=${role}` +
      `&nl_query=${nl_query}` +
      `&include_child_queries=${include_child_queries}` +
      (exclude_zero_count ? `&exclude_zero_count=true` : "") +
      (exclude_failed ? `&exclude_failed=true` : "");

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const getCampaignVolumes = async ({ args }) => {
    const {
      federationId,
      organizationId,
      page_size,
      scan_index_forward,
      Identifier,
      contributor_org_id,
      query_id,
      start_date,
      last_key,
      role,
      campaign_name,
      nl_query = false,
      include_child_queries = true,
      exclude_zero_count,
      exclude_failed,
    } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query/campaign-volumes?` +
      new URLSearchParams({
        federation_id: federationId,
        organization_id: organizationId,
      }) +
      `&identifier=${Identifier}` +
      `&contributor_org_id=${contributor_org_id}` +
      `&query_id=${query_id}` +
      `&campaign_name=${campaign_name}` +
      `&start_date=${start_date}` +
      `&last_key=${last_key && last_key !== "undefined" ? new Date(last_key).toISOString().replace(/\.\d+Z$/, "Z") : last_key}` +
      `&page_size=${page_size}` +
      `&scan_index_forward=${scan_index_forward}` +
      `&role=${role}` +
      `&nl_query=${nl_query}` +
      `&include_child_queries=${include_child_queries}` +
      (exclude_zero_count ? `&exclude_zero_count=true` : "") +
      (exclude_failed ? `&exclude_failed=true` : "");

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const triggerForQueryPercentage = async ({ args }) => {
    const {
      federationId,
      organizationId,
      page_size,
      scan_index_forward,
      role,
    } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query/running?` +
      new URLSearchParams({
        federation_id: federationId,
        organization_id: organizationId,
      }) +
      `&page_size=${page_size}` +
      `&scan_index_forward=${scan_index_forward}` +
      `&role=${role}`;

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const getQueryById = async ({ args }) => {
    const { organizationId, query_id } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query/${query_id}?` +
      new URLSearchParams({ organization_id: organizationId });

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const getLimitsUsage = async ({ args }) => {
    const { organizationId, federationId, queryType, matchId } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query/limits-usage?` +
      new URLSearchParams({
        organization_id: organizationId,
        federation_id: federationId,
        query_type: queryType,
        ...(matchId ? { match_id: matchId } : {}),
      });

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const getgenerativeHistory = async ({ args }) => {
    const {
      federationId,
      organizationId,
      page_size,
      scan_index_forward,
      Identifier,
      query_id,
      start_date,
      last_key,
      role,
      campaign_name,
      include_child_queries,
    } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query?` +
      new URLSearchParams({
        federation_id: federationId,
        organization_id: organizationId,
      }) +
      `&identifier=${Identifier}` +
      `&query_id=${query_id}` +
      `&start_date=${start_date}` +
      `&last_key=${last_key && last_key !== "undefined" ? new Date(last_key).toISOString().replace(/\.\d+Z$/, "Z") : last_key}` +
      `&page_size=${page_size}` +
      `&scan_index_forward=${scan_index_forward}` +
      `&nl_query=${true}` +
      `&campaign_name=${campaign_name}` +
      `&role=${role}` +
      `&include_child_queries=${include_child_queries}`;

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const getrunningquery = async ({ args }) => {
    const { federationId, organizationId, page_size, role } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query/running?` +
      new URLSearchParams({
        federation_id: federationId,
        organization_id: organizationId,
      }) +
      `&page_size=${page_size}` +
      `&scan_index_forward=${false}` +
      `&role=${role}`;

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const getPermissionsbyrole = async ({ args }) => {
    const { userRole } = args;
    const url = `${Atom_proxy}/api/v1/spa/registry/rbac/group/${userRole}`;
    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });
    if (!res.ok) {
      if (res.status === 403) {
        setRbacAccessDenied(true);
        throw new Error("RBAC_ACCESS_DENIED");
      }
      handleError(res);
    }
    const data = await res.json();
    return data;
  };
  const putBuckets = async ({ args }) => {
    const { body, organizationId } = args;
    const url =
      `${Atom_proxy}/api/v1/spa/fqs/buckets?` +
      new URLSearchParams({ organization_id: organizationId });
    const res = await fetch(url, {
      method: "POST",
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      if (res.status === 401) {
        clearAuth();
      }
      let errorMessage = `Error ${res.status}`;
      try {
        const errBody = await res.json();
        if (errBody?.message) errorMessage = errBody.message;
        else if (errBody?.error) errorMessage = errBody.error;
      } catch {}
      const error: any = new Error(errorMessage);
      // e.g. a 400 "table is disabled" rejection (see tablelimits on the FQS
      // side) vs. a 5xx infra failure — matches the putquery/putCompoundQuery
      // convention so callers can tell those apart if they need to.
      error.status = res.status;
      throw error;
    }

    const data = await res.json();
    return data;
  };
  const putFederation = async ({ args }) => {
    const { body } = args;
    try {
      const res = await fetch(`${Atom_proxy}/api/v1/spa/registry/federations`, {
        method: "POST",
        headers: await getHeaders(),
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        handleError(res);
        return null;
      }

      const data = await res.json();

      return data;
    } catch (error) {
      console.error(" Fetch Error:", error);
      return null;
    }
  };
  const putCampaignName = async ({ args }) => {
    const { body, organizationId } = args;

    try {
      const url =
        `${Atom_proxy}/api/v1/spa/fqs/campaigns?` +
        new URLSearchParams({
          organization_id: organizationId,
        });

      const res = await fetch(url, {
        method: "POST",
        headers: await getHeaders(),
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        let errorMessage = `Request failed with status: ${res.status}`;
        try {
          const errorBody = await res.json();
          if (errorBody?.message) errorMessage = errorBody.message;
          else if (errorBody?.error) errorMessage = errorBody.error;
        } catch {}
        throw new Error(errorMessage);
      }

      return await res.json();
    } catch (error) {
      throw error;
    }
  };

  const putCategory = async ({ args }) => {
    const { body, organizationId } = args;

    try {
      const url =
        `${Atom_proxy}/api/v1/spa/fqs/categories?` +
        new URLSearchParams({
          organization_id: organizationId,
        });

      const res = await fetch(url, {
        method: "POST",
        headers: await getHeaders(),
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        let errorMessage = `Request failed with status: ${res.status}`;
        try {
          const errorBody = await res.json();
          if (errorBody?.message) errorMessage = errorBody.message;
          else if (errorBody?.error) errorMessage = errorBody.error;
        } catch {}
        throw new Error(errorMessage);
      }

      return await res.json();
    } catch (error) {
      throw error;
    }
  };

  const putquery = async ({ args }) => {
    const { body, fedId, orgId, intermediate, applicationId: appId } = args;

    const baseUrl = intermediate
      ? `${Atom_proxy}/api/v1/spa/fqs/query/intermediate?`
      : `${Atom_proxy}/api/v1/spa/fqs/query?`;

    const queryURL =
      baseUrl +
      new URLSearchParams({
        federation_id: fedId,
        organization_id: orgId,
        ...(appId ? { application_id: appId } : {}),
      });

    const res = await fetch(queryURL, {
      method: "POST",
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const rawText = await res.text();
      let errorMessage = rawText || res.statusText || `API Error ${res.status}`;
      try {
        const errorBody = JSON.parse(rawText);
        if (errorBody?.message) errorMessage = errorBody.message;
        else if (errorBody?.error) errorMessage = errorBody.error;
      } catch {}
      const error: any = new Error(errorMessage);
      // Callers (e.g. NewQuery's invokeQuery) need the status to tell a hard
      // infra/server failure (5xx — nothing the user can do but retry later)
      // from a business-rule rejection (4xx — e.g. a limit reached, or a
      // predicate-validation warning the user can knowingly override).
      error.status = res.status;
      throw error;
    }

    return await res.json();
  };

  const putCompoundQuery = async ({ args }) => {
    const { body, fedId, orgId, applicationId: appId } = args;

    const queryURL =
      `${Atom_proxy}/api/v1/spa/fqs/query/aggregate?` +
      new URLSearchParams({
        federation_id: fedId,
        organization_id: orgId,
        ...(appId ? { application_id: appId } : {}),
      });

    const res = await fetch(queryURL, {
      method: "POST",
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      if (res.status === 401) clearAuth();
      const rawText = await res.text();
      let errorMessage = rawText || res.statusText || `API Error ${res.status}`;
      try {
        const errorBody = JSON.parse(rawText);
        if (errorBody?.message) errorMessage = errorBody.message;
        else if (errorBody?.error) errorMessage = errorBody.error;
      } catch {}
      const error: any = new Error(errorMessage);
      // Compound/nested queries hit the same table-limit enforcement as
      // putquery (HandleCreateAggregateItem calls enforceTableQueryLimits),
      // so mirror its parsing so "Query limit reached" / "This table is
      // disabled" reach the user instead of a raw unparsed JSON blob.
      error.status = res.status;
      throw error;
    }

    return await res.json();
  };

  const putqueryAI = async ({ args }) => {
    const { body, organizationId, applicationId: appId } = args;
    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query/intermediate/text?` +
      new URLSearchParams({
        organization_id: organizationId,
        user_name: user?.name || "",
        ...(appId ? { application_id: appId } : {}),
      });
    const res = await fetch(url, {
      method: "POST",
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      let errorMessage = `Request failed with status: ${res.status}`;
      try {
        const errorBody = await res.json();
        if (errorBody?.message) errorMessage = errorBody.message;
      } catch {}
      throw new Error(errorMessage);
    }

    const data = await res.json();
    return data;
  };

  const draftNLQ = async ({ args }) => {
    const { body, organizationId, applicationId: appId } = args;
    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query/intermediate/text/draft?` +
      new URLSearchParams({
        organization_id: organizationId,
        ...(appId ? { application_id: appId } : {}),
      });
    const res = await fetch(url, {
      method: "POST",
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      let errorMessage = `Request failed with status: ${res.status}`;
      try {
        const errorBody = await res.json();
        if (errorBody?.message) {
          errorMessage = errorBody.message;
        } else if (errorBody?.issues?.length) {
          errorMessage = errorBody.issues.join(" ");
          if (errorBody.hint) errorMessage += `\n${errorBody.hint}`;
        }
      } catch {}
      throw new Error(errorMessage);
    }

    const data = await res.json();
    return data;
  };

  const validateNLQ = async ({ args }) => {
    const { draft_id, input_text, organizationId, applicationId: appId } = args;
    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query/intermediate/text/validate?` +
      new URLSearchParams({
        organization_id: organizationId,
        ...(appId ? { application_id: appId } : {}),
      });
    const res = await fetch(url, {
      method: "POST",
      headers: await getHeaders(),
      body: JSON.stringify({ draft_id, input_text }),
    });

    if (!res.ok) {
      let errorMessage = `Request failed with status: ${res.status}`;
      try {
        const errorBody = await res.json();
        if (errorBody?.message) {
          errorMessage = errorBody.message;
        } else if (errorBody?.issues?.length) {
          errorMessage = errorBody.issues.join(" ");
          if (errorBody.hint) errorMessage += `\n${errorBody.hint}`;
        }
      } catch {}
      throw new Error(errorMessage);
    }

    return await res.json();
  };

  const executeNLQ = async ({ args }) => {
    const {
      draft_id,
      conversation_id,
      organizationId,
      applicationId: appId,
    } = args;
    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query/intermediate/text/execute?` +
      new URLSearchParams({
        organization_id: organizationId,
        ...(appId ? { application_id: appId } : {}),
      });
    const res = await fetch(url, {
      method: "POST",
      headers: await getHeaders(),
      body: JSON.stringify({ draft_id, conversation_id }),
    });

    if (!res.ok) {
      let errorMessage = `Request failed with status: ${res.status}`;
      try {
        const errorBody = await res.json();
        if (errorBody?.message) {
          errorMessage = errorBody.message;
        } else if (errorBody?.issues?.length) {
          errorMessage = errorBody.issues.join(" ");
          if (errorBody.hint) errorMessage += `\n${errorBody.hint}`;
        }
      } catch {}
      throw new Error(errorMessage);
    }

    const data = await res.json();
    return data;
  };

  const postIntermediateQuery = async ({ args }) => {
    const {
      conversationId,
      federationId,
      inquirerOrganizationId,
      inquirerBucketId,
      contributorBucket,
      identifier,
      objective,
      inputText,
      override_empty_predicate,
      llm_provider,
      llm_model,
      campaignName,
      canonical_identity_type,
    } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/query/intermediate/text?` +
      new URLSearchParams({
        organization_id: inquirerOrganizationId,
        user_name: user?.name || "",
      });
    const res = await fetch(url, {
      method: "POST",
      headers: await getHeaders(),
      body: JSON.stringify({
        conversation_id: conversationId,
        federation_id: federationId,
        inquirer_organization_id: inquirerOrganizationId,
        inquirer_bucket_id: inquirerBucketId,
        contributor_bucket: contributorBucket,
        identifier,
        canonical_identity_type,
        objective,
        llm_provider,
        llm_model,
        nl_query_params: { input_text: inputText },
        override_empty_predicate: override_empty_predicate || false,
        campaign_name: campaignName,
      }),
    });

    if (!res.ok) {
      let errorMessage = `Request failed with status: ${res.status}`;
      try {
        const errorBody = await res.json();
        if (errorBody?.message) errorMessage = errorBody.message;
      } catch {}
      throw new Error(errorMessage);
    }

    return await res.json();
  };

  const getIdentifiers = async ({ args }) => {
    const { federationId, organizationId, getFullResponse } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/fqs/identifiers?last_key&` +
      new URLSearchParams({
        federation_id: federationId,
        organization_id: organizationId,
      });

    const [res, identityMap] = await Promise.all([
      fetch(url, { method: "GET", headers: await getHeaders() }),
      resolveIdentityTypesMap(organizationId),
    ]);

    if (!res.ok) {
      handleError(res);
    }

    const data = await res.json();
    const enrichInner = (inner: any) =>
      inner
        ? {
            ...inner,
            canonical_identity_types: (
              inner.canonical_identity_types ?? []
            ).map((key: string) => ({
              key,
              value: identityMap.get(key) ?? key,
            })),
          }
        : inner;

    if (getFullResponse) return { ...data, data: enrichInner(data?.data) };
    return enrichInner(data?.data);
  };

  const getFAQ = async () => {
    const url = `${Atom_proxy}/api/v1/spa/registry/faq`;
    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });

    if (!res.ok) {
      handleError(res);
    }
    const data = await res.json();
    return data;
  };
  const putOrganizationsByFederationID = async ({ args }) => {
    const { federationID, body } = args;

    const url =
      `${Atom_proxy}/api/v1/spa/registry/federationmembers/federations?` +
      new URLSearchParams({
        federation_id: federationID,
      });

    const res = await fetch(url, {
      method: "POST",
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  const checkServiceHealth = async ({
    args,
  }: { args?: { service?: string; refresh?: boolean } } = {}) => {
    const { service, refresh = true } = args || {};
    const url = `${Atom_proxy}/api/v1/health`;
    const res = await fetch(url, {
      method: "POST",
      headers: await getHeaders(),
      body: JSON.stringify({ refresh, ...(service ? { service } : {}) }),
    });
    if (!res.ok) {
      handleError(res);
    }
    return await res.json();
  };

  const getHealthCheck = async () => {
    const url = `${Atom_proxy}/api/v1/health/latest`;
    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });
    if (!res.ok) {
      handleError(res);
    }
    return await res.json();
  };

  const getPqsHealthCheck = async () => {
    const url = `${Atom_proxy}/api/v1/spa/fqs/dbhealthcheck/items?latest=true`;
    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });
    if (!res.ok) {
      handleError(res);
    }
    return await res.json();
  };

  const getLlmModels = async ({
    args,
  }: {
    args: { organizationId: string };
  }) => {
    const { organizationId } = args;

    const params = new URLSearchParams({
      organization_id: organizationId,
    });

    const url = `${Atom_proxy}/api/v1/spa/fqs/llm/providers?${params}`;

    const res = await fetch(url, {
      method: "GET",
      headers: await getHeaders(),
    });
    if (!res.ok) {
      handleError(res);
    }
    return await res.json();
  };

  const refreshHealthCheck = async () => {
    const url = `${Atom_proxy}/api/v1/health`;
    const res = await fetch(url, {
      method: "POST",
      headers: await getHeaders(),
      body: JSON.stringify({ refresh: true }),
    });
    if (!res.ok) {
      handleError(res);
    }

    if (!res.ok) {
      handleError(res);
    }

    return await res.json();
  };

  return (
    <AuthContext.Provider
      value={{
        isAuth: isAuthenticated,
        isAuthPending: isLoading,
        rbacAccessDenied,
        user,
        tenant: tenants && tenants[tenantId],
        applicationId,
        vaultAddress,
        appMetadata:
          user &&
          user[`https://users/app_metadata`][AUTH0_CLIENT_ID] &&
          user[`https://users/app_metadata`][AUTH0_CLIENT_ID]["tenants"],
        clearAuth,
        getUsers,
        putUser,
        //getVaultAddress,
        getUploadStatus,
        // getAttributeOptions,
        getConfig,
        getNumRecords,
        getSchema,
        patchSchema,
        deleteSchema,
        getGlobalSchema,
        getProvenanceTrail,
        uploadCsv,
        getFederations,
        getFederationsByOrgID,
        getProvenanceTrailCSV,
        getProvenanceTrailPDF,
        getOrgByFederationID,
        getIndexOrganizations,
        getProvenanceTrialEvents,
        putDatasource,
        getTablesByDataSourceId,
        getDatasource,
        getRoles,
        getTablesByName,
        getDatasourceById,
        getAudienceGeneration,
        putBuckets,
        putmatchRate,
        getOrganizations,
        getAttributes,
        putquery,
        getDataByQueryId,
        getquery,
        getLimitsUsage,
        updatequery,
        updateOrganizationById,
        deletequery,
        patchQueryCohort,
        patchQueryCpm,
        rerunQuery,
        transferFiles,
        getorganizationById,
        getAdserversByOrgId,
        deleteAdserverByOrgId,
        postAdserverByOrgId,
        getrecompute,
        getdatasourceStatus,
        deleteDatasource,
        getactivationconnectorsforadmin,
        putactivationconnectorsforadmin,
        getbucketById,
        getDatasourceByFedId,
        getBucketNames,
        getrunningquery,
        getmatchrates,
        getBucketsCounts,
        getmatchratequery,
        putEngage,
        putConnector,
        merkleProofapi,
        getPermissionsbyrole,
        getIdentifiers,
        getSelectAttributes,
        getAttrDistribution,
        getGlobalSchemawithFilters,
        getApplication,
        getAllApplications,
        getFederationsByApplicationIds,
        getFednDetails,
        getLogoFederationID,
        getgenerativeHistory,
        getQueryById,
        putqueryAI,
        draftNLQ,
        validateNLQ,
        executeNLQ,
        postIntermediateQuery,
        putCompoundQuery,
        getFAQ,
        putFederation,
        putOrganizationsByFederationID,
        putCampaignName,
        getCampaignNames,
        putCategory,
        getCategories,
        getFederationPublished,
        getFederationSummary,
        triggerForQueryPercentage,
        getCampaignVolumes,
        getCampaignReportsCSV,
        getActivationHistoryExport,
        getDashboard,
        getFederationDetails,
        getOrganizationDetails,
        getAdserverDetails,
        getEventDayLevelCount,
        getFederationDayLevelCount,
        getOrganizationDayLevelCount,
        getAdserverDayLevelCount,
        getOrganizationActivationEnrichmentDayLevelCount,
        tableGetEventDayLevelCount,
        tableGetFederationMonthLevelCount,
        tableGetOrganizationMonthLevelCount,
        tableGetAdserverMonthLevelCount,
        tableGetFederationDayLevelCount,
        tableGetOrganizationDayLevelCount,
        tableGetAdserverDayLevelCount,
        tableGetOrganizationActivationEnrichmentMonthLevelCount,
        tableGetOrganizationActivationEnrichmentDayLevelCount,
        orgGetDashboard,
        orgGetEventDayLevelCount,
        orgGetFederationDayLevelCount,
        orgGetOrganizationDayLevelCount,
        orgGetAdserverDayLevelCount,
        orgTableGetEventDayLevelCount,
        orgGetFederationDetails,
        orgGetOrganizationDetails,
        orgGetAdserverDetails,
        orgGetContributorCountValues,
        orgTableGetFederationMonthLevelCount,
        orgTableGetOrganizationMonthLevelCount,
        orgTableGetAdserverMonthLevelCount,
        orgTableGetFederationDayLevelCount,
        orgTableGetOrganizationDayLevelCount,
        orgTableGetAdserverDayLevelCount,
        orgTableGetOrganizationActivationEnrichmentMonthLevelCount,
        orgTableGetOrganizationActivationEnrichmentDayLevelCount,
        getHealthCheck,
        getPqsHealthCheck,
        checkServiceHealth,
        refreshHealthCheck,
        getLlmModels,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};
